import type { EntityAdminRecord } from '@src/components/EntityAdmin/types';
import { get } from 'lodash-es';
import { memo, useCallback, useMemo } from 'react';
import type { Security } from '../../../../../types';
import { EMPTY_OBJECT } from '../../../../../utils';
import { SymbolSelector, SymbolSelectorAppearance } from '../../../../SymbolSelector';
import { useEntityAdminDrawerData } from '../../EntityAdminDrawerContext';
import type { SymbolSelectorDrawerOption } from '../types';
import { drawerContentMemoComparison, type DrawerContentProps } from './utils';

const SymbolSelectorContentInner = <TDrawerRecord extends EntityAdminRecord>({
  drawerOption,
  form,
  isEditing,
  onFormUpdate,
}: DrawerContentProps<SymbolSelectorDrawerOption<TDrawerRecord>, TDrawerRecord>) => {
  const drawerData = useEntityAdminDrawerData();

  const symbol = useMemo(() => {
    const value = get(form, drawerOption.field);
    return typeof value === 'string' && value !== '' ? value : undefined;
  }, [form, drawerOption.field]);

  const disabled = useMemo(() => {
    return (drawerOption.disabledWhenEditing && isEditing) || drawerOption.getIsDisabled?.(form) === true;
  }, [drawerOption, form, isEditing]);

  const availableSecurities = useMemo(() => {
    return drawerOption.getAvailableSecurities?.(form, isEditing);
  }, [drawerOption, form, isEditing]);

  /**
   * When `disableKeyedOptions` is set, compute the set of symbols to disable based on EntityAdmin context:
   * - Adding a new entity whose field matches `entityIDField`: disable all symbols already used by the blotter.
   * - Adding a child whose field matches `childIDField`: disable symbols already used as children of the parent.
   * Only applies while adding (not editing).
   */
  const { gridApi, entityIDField, childIDField, addingChildEntity = false, parentEntity } = drawerData ?? EMPTY_OBJECT;
  const disabledKeyedSymbols = useMemo<Set<string> | undefined>(() => {
    if (!drawerOption.disableKeyedOptions || isEditing || gridApi == null) {
      return undefined;
    }
    if (gridApi.isDestroyed()) {
      return undefined;
    }

    if (addingChildEntity && childIDField != null && parentEntity != null) {
      if (String(drawerOption.field) !== String(childIDField)) {
        return undefined;
      }
      const symbols = new Set<string>();
      for (const child of parentEntity.getAllChildren(gridApi)) {
        const val = get(child.data, childIDField) as unknown;
        if (typeof val === 'string') {
          symbols.add(val);
        }
      }
      return symbols.size > 0 ? symbols : undefined;
    }

    if (!addingChildEntity && entityIDField != null) {
      if (String(drawerOption.field) !== String(entityIDField)) {
        return undefined;
      }
      const symbols = new Set<string>();
      gridApi.forEachNode(node => {
        const val = get(node.data, entityIDField) as unknown;
        if (typeof val === 'string') {
          symbols.add(val);
        }
      });
      return symbols.size > 0 ? symbols : undefined;
    }

    return undefined;
  }, [
    drawerOption.disableKeyedOptions,
    drawerOption.field,
    isEditing,
    gridApi,
    entityIDField,
    childIDField,
    addingChildEntity,
    parentEntity,
  ]);

  const isItemDisabled = useCallback(
    (security: Security) => {
      if (drawerOption.getIsItemDisabled?.(security, form, isEditing)) {
        return true;
      }
      if (disabledKeyedSymbols?.has(security.Symbol)) {
        return true;
      }
      return false;
    },
    [drawerOption, form, isEditing, disabledKeyedSymbols]
  );

  const placeholder = useMemo(() => {
    // If a symbol already exists on the record, show it (even if not in filtered list)
    if (symbol) {
      return symbol;
    }
    // Otherwise show the configured placeholder
    if (isEditing) {
      return drawerOption.placeholder;
    }
    return drawerOption.placeholder;
  }, [drawerOption.placeholder, isEditing, symbol]);

  const handleSymbolChanged = useCallback(
    (newSymbol?: string) => {
      onFormUpdate(drawerOption.field, newSymbol ?? '');
      drawerOption.onChange?.(newSymbol, onFormUpdate, form);
    },
    [drawerOption, form, onFormUpdate]
  );

  return (
    <SymbolSelector
      data-testid={`entity-admin-drawer-${String(drawerOption.field)}`}
      symbol={symbol}
      onSymbolChanged={handleSymbolChanged}
      appearance={SymbolSelectorAppearance.Basic}
      disabled={disabled}
      availableSecurities={availableSecurities}
      placeholder={placeholder}
      showFavorites={drawerOption.showFavorites ?? true}
      showGroups={drawerOption.showGroups ?? true}
      showClear={drawerOption.showClear ?? true}
      closeOnInputBlur={false}
      dropdownWidth="100%"
      isItemDisabled={isItemDisabled}
    />
  );
};

export const SymbolSelectorContent = memo(SymbolSelectorContentInner, drawerContentMemoComparison) as <
  TDrawerRecord extends EntityAdminRecord,
>(
  props: DrawerContentProps<SymbolSelectorDrawerOption<TDrawerRecord>, TDrawerRecord>
) => JSX.Element;
